import React, { useMemo, useState } from 'react';
import { CommentData, MentionUser } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import Comment from './Comment';
import ReplyForm from './ReplyForm';
import { CommentIcon } from './playerIcons';
import { buildCommentTree, flattenTree } from '../utils/commentTree';

interface CommentSectionProps {
  comments: CommentData[];
  onSeek: (time: number) => void;
  onDeleteComment: (commentId: string) => void;
  onEditComment?: (commentId: string, newText: string) => void;
  onJumpAndShow: (comment: CommentData) => void;
  onReply?: (parentId: string, text: string, file?: File) => Promise<void>;
  currentUserId?: string | null;
  fps?: number;
  users?: MentionUser[];
}

/** Flatten nested replies[] from API into a flat list for tree building */
function flattenApiComments(comments: CommentData[]): CommentData[] {
  const result: CommentData[] = [];
  const walk = (list: CommentData[]) => {
    for (const c of list) {
      const { replies, ...rest } = c;
      result.push(rest as CommentData);
      if (replies && replies.length > 0) walk(replies);
    }
  };
  walk(comments);
  return result;
}

const CommentSection: React.FC<CommentSectionProps> = ({
  comments,
  onSeek,
  onDeleteComment,
  onEditComment,
  onJumpAndShow,
  onReply,
  currentUserId,
  fps = 24,
  users = [],
}) => {
  const { t } = useTranslation();
  const [sortMode, setSortMode] = useState<'timecode' | 'date'>('timecode');
  const [replyingToId, setReplyingToId] = useState<string | null>(null);

  const flatComments = useMemo(() => flattenApiComments(comments), [comments]);

  const sortedRoots = useMemo(() => {
    const tree = buildCommentTree(flatComments);
    const getTime = (c: CommentData) => {
      const v = Number(c.timeInSeconds);
      return Number.isFinite(v) ? v : 0;
    };
    const getDate = (c: CommentData) => {
      if (!c.createdAt) return 0;
      const ms = new Date(c.createdAt).getTime();
      return Number.isFinite(ms) ? ms : 0;
    };

    const sorted = [...tree].sort((a, b) => {
      if (sortMode === 'date') {
        const d = getDate(a) - getDate(b);
        if (d !== 0) return d;
        return getTime(a) - getTime(b);
      }
      const tDiff = getTime(a) - getTime(b);
      if (tDiff !== 0) return tDiff;
      return getDate(a) - getDate(b);
    });

    return flattenTree(sorted);
  }, [flatComments, sortMode]);

  return (
    <div className="bg-gray-800 flex flex-col h-full">
      <div className="p-4 flex-shrink-0 border-b border-gray-700 flex items-center justify-between gap-2">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <CommentIcon className="w-5 h-5" />
          {t('player.comments')}
          <span className="text-sm font-normal text-gray-400">({flatComments.length})</span>
        </h3>
        <div className="flex rounded-lg overflow-hidden border border-gray-600 text-xs">
          <button
            type="button"
            onClick={() => setSortMode('timecode')}
            className={`px-2 py-1 ${sortMode === 'timecode' ? 'bg-indigo-600 text-white' : 'bg-gray-900 text-gray-400'}`}
          >
            {t('player.sortByTimecode')}
          </button>
          <button
            type="button"
            onClick={() => setSortMode('date')}
            className={`px-2 py-1 ${sortMode === 'date' ? 'bg-indigo-600 text-white' : 'bg-gray-900 text-gray-400'}`}
          >
            {t('player.sortByDate')}
          </button>
        </div>
      </div>

      <div className="flex-grow overflow-y-auto p-4 space-y-3">
        {sortedRoots.length > 0 ? (
          sortedRoots.map((comment) => (
            <div key={comment.id}>
              <Comment
                comment={comment}
                depth={comment.depth}
                onSeek={onSeek}
                canDelete={!currentUserId || comment.authorId === currentUserId}
                canEdit={!currentUserId || comment.authorId === currentUserId}
                onDelete={() => onDeleteComment(comment.id)}
                onEdit={onEditComment}
                onJumpAndShow={() => onJumpAndShow(comment)}
                onReply={onReply ? () => setReplyingToId(comment.id) : undefined}
                fps={fps}
              />
              {replyingToId === comment.id && onReply && (
                <div className="mt-2 ml-3">
                  <ReplyForm
                    users={users}
                    onCancel={() => setReplyingToId(null)}
                    onSubmit={async (text, file) => {
                      await onReply(comment.id, text, file);
                      setReplyingToId(null);
                    }}
                  />
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="text-gray-400 text-center pt-10 select-none">{t('player.noComments')}</p>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
